import { Wrench, Clock, RefreshCw } from 'lucide-react'

type MaintenanceSource = 'env' | 'query' | 'localStorage' | 'window'

interface MaintenancePageProps {
  source?: MaintenanceSource
}

const getSourceHint = (source?: MaintenanceSource): string => {
  if (source === 'query') return 'Aktiviert via URL-Parameter (?maintenance=true).'
  if (source === 'localStorage') return 'Aktiviert via Browser-Speicher (maintenance_mode=true).'
  if (source === 'window') return 'Aktiviert via Runtime-Flag (window.__MAINTENANCE_MODE__).'
  return 'Aktiviert via Server-Konfiguration.'
}

const isLocallyResolvableSource = (source?: MaintenanceSource): boolean => {
  return source === 'query' || source === 'localStorage' || source === 'window'
}

export default function MaintenancePage({ source }: MaintenancePageProps) {
  const canResolveLocally = isLocallyResolvableSource(source)

  const disableLocalMaintenance = () => {
    if (typeof window === 'undefined') {
      return
    }

    window.localStorage.removeItem('maintenance_mode')
    ;(window as Window & { __MAINTENANCE_MODE__?: unknown }).__MAINTENANCE_MODE__ = false

    const url = new URL(window.location.href)
    url.searchParams.delete('maintenance')
    window.location.replace(url.toString())
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-night-950 flex items-center justify-center px-4">
      <div className="w-full max-w-2xl rounded-2xl border border-gray-200 dark:border-night-800 bg-white dark:bg-night-900 shadow-xl p-8 sm:p-10 text-center">
        <div className="mx-auto mb-5 w-14 h-14 rounded-xl bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center">
          <Wrench className="w-7 h-7 text-orange-600 dark:text-orange-400" />
        </div>

        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-slate-100 mb-3">
          Wir sind kurz in Wartung
        </h1>

        <p className="text-gray-600 dark:text-slate-300 mb-3">
          Aktuell nehmen wir technische Anpassungen vor, um die Trainingsplattform weiter zu verbessern.
        </p>
        <p className="text-gray-600 dark:text-slate-300 mb-7">
          Bitte schau demnaechst wieder vorbei.
        </p>

        <div className="grid sm:grid-cols-2 gap-3 text-sm mb-7">
          <div className="rounded-lg bg-gray-100 dark:bg-night-850 px-4 py-3 text-gray-700 dark:text-slate-200">
            <span className="font-semibold">Website:</span> muay-thai.app
          </div>
          <div className="rounded-lg bg-gray-100 dark:bg-night-850 px-4 py-3 text-gray-700 dark:text-slate-200">
            <span className="font-semibold">App:</span> app.muay-thai.app
          </div>
        </div>

        <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">
          Bei dringenden Fragen: <a className="underline hover:text-purple-600 dark:hover:text-ocean-400" href="mailto:beta@muay-thai.app">beta@muay-thai.app</a>
        </p>

        <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-slate-400 mb-4">
          <Clock className="w-4 h-4" />
          <span>Der Zugriff ist voruebergehend eingeschraenkt</span>
        </div>

        <div className="mb-5 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 px-4 py-3 text-sm text-orange-900 dark:text-orange-200">
          <span className="font-semibold">Hinweis:</span> {getSourceHint(source)}
        </div>

        {canResolveLocally && (
          <div className="mb-5 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-4 py-3 text-sm text-blue-900 dark:text-blue-200">
            <p className="mb-2">
              Dieser Wartungsmodus scheint lokal aus Browser/URL zu kommen.
            </p>
            <button
              type="button"
              onClick={disableLocalMaintenance}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors"
            >
              Lokalen Wartungsmodus deaktivieren
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Seite neu laden
        </button>
      </div>
    </div>
  )
}
